import { DOCUMENT, NgClass } from '@angular/common';
import { AfterViewInit, Component, OnInit, inject } from '@angular/core';

import { MainCartService } from '@services/main-cart.service';
import { StepperService } from '@services/stepper.service';

import { Observable, Subject, forkJoin, map, mergeMap } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { DataCyDirective } from '../../common/directives/data-cy.directive';
import { Step } from './steps/step';

@Component({
    selector: 'checkout-stepper',
    templateUrl: './stepper.component.html',
    styleUrl: 'stepper.component.scss',
    standalone: true,
    imports: [DataCyDirective, NgClass],
})
export class StepperComponent implements OnInit, AfterViewInit {
    public steps: Step[] = [];
    public currentStep: Step = null;

    private stepperService: StepperService = inject(StepperService);
    private cartService: MainCartService = inject(MainCartService);
    private _ngUnsubscribe$ = new Subject();
    private document: Document = inject(DOCUMENT);

    ngOnInit(): void {
        this.getSteps().subscribe((steps) => {
            this.steps = steps;
        });

        this.stepperService.stepsUpdateSubject
            .pipe(
                takeUntil(this._ngUnsubscribe$),
                mergeMap(() => this.getSteps()),
            )
            .subscribe((steps) => {
                this.steps = steps;
            });

        this.stepperService.currentStepSubject.pipe(takeUntil(this._ngUnsubscribe$)).subscribe((step) => {
            this.currentStep = step;
        });
    }

    public ngAfterViewInit(): void {
        this.cartService.cartQuoteSubscription.pipe(debounceTime(500)).subscribe(() => {
            this.stepperService.refresh();
        });
    }

    public goToStep(step: Step): void {
        step.canAccess().subscribe((canAccess) => {
            if (canAccess) {
                this.stepperService.goToStep(step);
            }
        });
    }

    public getSteps(): Observable<Step[]> {
        const enabledStepsObs: any = {};
        if (!this.document.location.href.includes('/tunnel/redirect')) {
            this.stepperService.allSteps.forEach((step) => {
                enabledStepsObs[step.routerLink] = step.isEnable();
            });
        }

        return forkJoin(enabledStepsObs).pipe(
            map((enabledResult) =>
                this.stepperService.allSteps.filter((step) => enabledResult[step.routerLink] && !step.isBypass),
            ),
            map((enabledResult) =>
                enabledResult.map((step, i) => {
                    step.order = i;
                    return step;
                }),
            ),
        );
    }

    public ngOnDestroy(): void {
        this._ngUnsubscribe$.next(null);
        this._ngUnsubscribe$.complete();
    }
}
